import { Trans, useTranslation } from 'react-i18next';
import { TrendingUp } from 'react-feather';

import {
  GetEventQuery as GetEvent,
} from '__generated__/graphql';

import { differenceInMinutes, isFuture } from 'date-fns';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

type Discount = GetEvent['event']['ticket_categories'][0]['tickets_for_sale']['0']['current_discount'];

export interface ScheduledDiscountLabelProps {
  discount: Discount;
  promotions: { end_date?: string | null; }[];
}

const ScheduledDiscountLabel = ({ discount, promotions }: ScheduledDiscountLabelProps) => {
  // Explicitly use frontend namespace, because this component is also used in the participant dashboard
  const { t } = useTranslation('frontend');
  const { formatNumber, parseDate } = useLocale();

  const levelIsActive = (level: Discount['levels'][0]) => {
    if (level.end_date !== null) {
      return isFuture(parseDate(level.end_date, { timezone: 'UTC' }));
    }

    return false;
  };

  const activeLevelIndex = discount.levels.findIndex(levelIsActive);
  const activeLevel = discount.levels[activeLevelIndex];

  if (!activeLevel) {
    // No discount active.
    return <></>;
  }

  const nextLevel = discount.levels[activeLevelIndex + 1];

  if (nextLevel && nextLevel.value >= activeLevel.value) {
    // Price is going to decrease or stay the same, don't show anything.
    return <></>;
  }

  const salesEndAfterDiscountEnds = promotions.reduce<boolean>((salesEndAfterDiscountEnds, promotion) => (
    salesEndAfterDiscountEnds || !promotion.end_date || promotion.end_date > activeLevel.end_date
  ), false);

  if (!salesEndAfterDiscountEnds) {
    // Sales end before discount ends, don't show anything.
    return <></>;
  }

  const minutesLeft = differenceInMinutes(parseDate(activeLevel.end_date, { timezone: 'UTC' }), new Date());
  const hoursLeft = Math.round(minutesLeft / 60);
  const daysLeft = Math.round(hoursLeft / 24);

  if (daysLeft > 7) {
    return <></>;
  }

  return (
    <UI.Label sc={{ small: true }}>
      <UI.Icon>
        <TrendingUp />
      </UI.Icon>
      {' '}
      <Trans
        i18nKey="frontend:selector.price_increase_in_days"
        values={{
          timeLeft: hoursLeft > 48
            ? t('selector.n_days', {
              count: daysLeft,
              value: formatNumber(daysLeft),
            })
            : (minutesLeft > 60
              ? t('selector.n_hours', {
                count: hoursLeft,
                value: formatNumber(hoursLeft),
              })
              : t('selector.n_minutes', {
                count: minutesLeft,
                value: formatNumber(minutesLeft),
              })),
        }}
      >
        <UI.Strong />
      </Trans>
    </UI.Label>
  );
};

export default ScheduledDiscountLabel;
